/**
 * Route guard for the manager tracking landing (`/dashboard/agm`)
 * (CRM-ROLE-VISIBILITY-DOCTRINE-V1 + OWNER-READ-DOWN). Mirrors
 * RequireRoutingSettings / RequireLeadCockpit: the pure decision lives in
 * decideManagerTrackingAccess (manager-tracking-access.ts) and keys off the
 * caller's role via the MANAGER_TRACKING_AUDIENCE_ROLES role-set -- the
 * leadership read tier owner / gm_agm / regional_head (doctrine rule 1,
 * hierarchy read-down). Any other role redirects to its own homePath.
 *
 * This REPLACES RequireNavSection section="agm" on the /dashboard/agm group so
 * the owner is admitted to the operational rollup by hierarchy (rule 1) WITHOUT
 * inheriting the AgmNav menu (rule 3, decoupled from me.navSections). gm_agm /
 * regional_head behaviour is byte-identical (they rendered before, they render
 * now); only the owner is newly admitted. It is a role-only gate: NO /me flag,
 * NO feature flag (internal leadership surface, default-on per ADR-0016).
 *
 * SEE-NOT-DO (doctrine rule 2): a READ gate only -- it grants no claim / assign /
 * qualify / stage authority on the manager surface. Backend route auth
 * (MANAGER_TRACKING_AUDIENCE_ROLES + requireAgmScope) remains the real security
 * boundary; this guard is UX cleanliness only.
 */

import { Navigate, Outlet } from 'react-router-dom';
import { useDashboardMeContext } from '@/dashboard/me-context';
import { decideManagerTrackingAccess } from '@/dashboard/manager-tracking/manager-tracking-access';
import type { DashboardMe } from '@/dashboard/types';

export interface RequireManagerTrackingViewProps {
  readonly loading: boolean;
  readonly error: string | null;
  readonly me: DashboardMe | null;
  /** Rendered when access is granted. Defaults to `<Outlet />`. */
  readonly children?: React.ReactNode;
}

/** Pure presentational view - testable via react-dom/server. */
export function RequireManagerTrackingView({
  loading,
  error,
  me,
  children,
}: RequireManagerTrackingViewProps): JSX.Element | null {
  const decision = decideManagerTrackingAccess({ loading, error, me });
  switch (decision.kind) {
    case 'wait':
      return null;
    case 'redirect':
      return <Navigate to={decision.to} replace />;
    case 'render':
      return <>{children ?? <Outlet />}</>;
    default: {
      const _exhaustive: never = decision;
      void _exhaustive;
      return null;
    }
  }
}

export default function RequireManagerTracking(): JSX.Element | null {
  const ctx = useDashboardMeContext();
  if (!ctx) {
    return <Navigate to="/login" replace />;
  }
  return (
    <RequireManagerTrackingView
      loading={ctx.loading}
      error={ctx.error}
      me={ctx.me}
    />
  );
}
